import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Api } from '../../api/Assinafy';
import { Page } from '../../layout';
import { Icon, Spinner, Button, Alert, Modal } from '../../components';
import {
  getActivePaidSubscriptionRestrictions,
  getDeletionErrorMessages,
  getRestrictionAccountIds,
  hasForceablePaidSubscriptionRestriction,
} from '../../utils/deletionRestrictions';

import './styles.scss';

const ProfileDelete = ({ scheduled }) => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [error, setError] = useState([])
  const [loading, setLoading] = useState(!scheduled && !!state)
  const [forceDeleteModal, setForceDeleteModal] = useState(false)
  const [paidRestrictions, setPaidRestrictions] = useState([])
  const [terminationError, setTerminationError] = useState(null)
  const terminatedAccountIdsRef = useRef(new Set())

  const handleDeleteSuccess = ({ data }) => {
    if (data.delete_status === 'done') {
      navigate('/logout')
    } else if (data.to_be_deleted_at) {
      navigate('/dashboard/profile/delete-scheduled')
    }
  }

  const handleDeleteError = (response) => {
    if (!response?.data) {
      return
    }

    const responseData = response.data
    const messages = getDeletionErrorMessages(responseData)
    setError(messages)

    if (hasForceablePaidSubscriptionRestriction(responseData)) {
      setPaidRestrictions(getActivePaidSubscriptionRestrictions(responseData))
      setForceDeleteModal(true)
    }
  }

  const requestDelete = (options = {}) =>
    Api('user/deleteSelf')(state, options.force ? { params: { force: true } } : {})

  const runDelete = (options = {}) => {
    setLoading(true)
    setError([])

    return requestDelete(options)
      .then(({ status, data: { data } }) => {
        if (status === 200) {
          handleDeleteSuccess({ data })
        }
      })
      .catch(({ response }) => {
        handleDeleteError(response)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    if (scheduled || !state) {
      return
    }

    runDelete()
  }, [state, scheduled, navigate])

  const handleForceDelete = () => {
    const accountIds = getRestrictionAccountIds(paidRestrictions)

    setLoading(true)
    setTerminationError(null)

    Promise.all(
      accountIds
        .filter((accountId) => !terminatedAccountIdsRef.current.has(accountId))
        .map((accountId) =>
          Api('subscription/terminate')(accountId).then((response) => {
            terminatedAccountIdsRef.current.add(accountId)
            return response
          })
        )
    )
      .then(() => {
        terminatedAccountIdsRef.current.clear()
        setForceDeleteModal(false)
        return runDelete({ force: true })
      })
      .catch((requestError) => {
        setLoading(false)
        setTerminationError(
          requestError?.response?.data?.message ||
          'Não foi possível cancelar a assinatura agora. Tente novamente em alguns minutos.'
        )
      })
  }

  const pageContent = () => {
    if (scheduled) {
      return (<>
        <Icon id="icon-date" size="88" />
        <h4 className="h4">
          Exclusão de conta agendada!
        </h4>
        <p>
          Em 30 dias, sua conta será excluída e você não poderá mais acessar os documentos assinados.
        </p>
        <p>
          Caso mude de ideia, você pode cancelar a exclusão da conta e manter todos os seus documentos disponíveis.
        </p>
        <Button
          size="lg"
          variant="primary"
          as={Link}
          to={'/dashboard'}
        >
          Continuar acessando Assinafy
          <Icon id="icon-arrow_forward" className="ms-2" size="18" />
        </Button>
      </>)
    }

    if (error.length && !loading) {
      return (
        <Button
          size="lg"
          variant="primary"
          as={Link}
          to="/dashboard/profile"
        >
          <Icon id="icon-arrow_back" className="me-2" size="18" />
          Voltar para minha conta
        </Button>
      )
    }

    if (loading) {
      return (<>
        <Spinner />
        Confirmando agendamento de exclusão da conta
      </>)
    }

    return (
      <Button
        size="lg"
        variant="primary"
        as={Link}
        to="/dashboard/profile"
      >
        <Icon id="icon-arrow_back" className="me-2" size="18" />
        Voltar para minha conta
      </Button>
    )
  }

  const restrictionMessages = paidRestrictions
    .map((restriction) => restriction.message)
    .filter(Boolean)

  return (
    <Page className="page-profile-delete"
      title="Exclusão de conta"
    >
      <Alert show={error.length} variant="warning">
        {error.map((message) => message)}
      </Alert>

      <div className="page-opening">
        {pageContent()}
      </div>

      <Modal.Alert
        show={forceDeleteModal}
        variant="warning"
        icon="icon-warning"
        title="Excluir sua conta mesmo assim?"
        description={(
          <>
            {restrictionMessages.map((message, index) => (
              <p key={index}>{message}</p>
            ))}
            <p>
              Ao confirmar, as assinaturas pagas serão canceladas imediatamente e o agendamento de exclusão da conta será realizado.
            </p>
          </>
        )}
        onClose={() => setForceDeleteModal(false)}
      >
        <Modal.Footer>
          {terminationError && <Alert variant="danger" className="w-100">{terminationError}</Alert>}
          <Button variant="light" onClick={() => setForceDeleteModal(false)}>
            CANCELAR
          </Button>
          <Button variant="danger" onClick={handleForceDelete} loading={loading}>
            <Icon id="icon-trash" className="me-1" size="16" />
            {terminationError ? 'TENTAR NOVAMENTE' : 'EXCLUIR MESMO ASSIM'}
          </Button>
        </Modal.Footer>
      </Modal.Alert>
    </Page>
  );
}

export default ProfileDelete;
